/**
 * GameScene — o protótipo cinza do M1.
 *
 * Regra de ouro: NENHUMA regra de jogo vive aqui. A cena lê o estado do core,
 * desenha retângulos e converte teclado em `Input`. Tudo o que mata, pontua ou
 * move mora em `@pixelraid/core` — é o que o M4 vai vestir com arte sem tocar
 * em comportamento.
 *
 * Efeitos visuais (shake, flash, piscar de invulnerabilidade) são cosméticos
 * do cliente e podem usar o relógio que quiserem: não tocam na simulação.
 */

import Phaser from 'phaser'
import { FUEL, LIVES, NEUTRAL_INPUT, PLAYER, SCREEN, SCROLL, TICK_MS, WORLD, dailySeed, fpToPx, type Input } from '@pixelraid/core'
import { onKey } from '../input/keys'
import { TouchControls } from '../input/touch'
import { TouchButton, isTouchDevice } from '../ui/buttons'
import { slotColor } from '../ui/players'
import { pixelText, preloadPixelFonts, setPixelSize, setPixelText } from '../ui/text'
import { RiverView } from '../terrain/river'
import { recordRun } from '../systems/records'
import { submitMessage, submitRun } from '../systems/season-client'
import { CoopInputs, CoopTimeline, GuestInputSender, predictTicks } from '../net/coop'
import type { SimState } from '@pixelraid/core'
import { CLASSIC_SEED } from './MenuScene'
import type { NetLink, PeerInfo } from '../net/NetLink'
import { AudioSystem } from '../systems/AudioSystem'
import { SimHost } from '../systems/SimHost'

interface Boom {
  x: number
  y: number
  ticks: number
}

const COLORS = {
  water: 0x10314a,
  land: 0x3c5a3c,
  landSafe: 0x46684a,
  planeAccent: 0xff8c1a,
  explosion: 0xffa030,
  hudPanel: 0x1c1c22,
  hudText: '#9ae6b4',
  fuelOk: 0x36c26e,
  fuelLow: 0xe04040,
  fuelFilling: 0xbdf0d0,
  barBack: 0x0a0a0c,
  ship: 0x9aa7b8,
  heli: 0x6b7d52,
  heliRotor: 0x1c241c,
  depot: 0xd8b23c,
  depotStripe: 0xc23a2e,
  bullet: 0xfff2c8,
  bridge: 0x8c8c94,
  bridgeTower: 0x5a5a62,
  jet: 0x2e3440,
  jetWarning: 0xe04040,
} as const

interface SceneData {
  seed?: number
  daily?: string | null
  players?: number
  race?: RaceConfig
  coop?: CoopConfig
  /** Índice da pista quando a partida vale para a season; null fora dela. */
  seasonTrack?: number | null
}

/** Config do co-op online (§12.2), montada pela LobbyScene. */
export interface CoopConfig {
  link: NetLink
  mySlot: number
  peers: PeerInfo[]
  /** Slot 0 é o host: ele simula e publica os inputs de todo mundo (§13.1). */
  isHost: boolean
}

/** Config da corrida online (§12.3), montada pela LobbyScene. */
export interface RaceConfig {
  link: NetLink
  myPeerId: string
  mySlot: number
  peers: PeerInfo[]
}

/** Último progresso conhecido de um oponente (10 Hz, §13.4). */
interface GhostState {
  slot: number
  distPx: number
  x: number
  fuel01: number
  alive: boolean
  score: number
}

/**
 * Amortecedor de jitter do `CoopTimeline`, em ticks. Repetido aqui porque a
 * previsão tem de cobrir exatamente ele — se os dois números divergirem, o
 * conserto do atraso desanda em silêncio.
 */
const COOP_BUFFER_TICKS = 3

/**
 * Piso e teto da profundidade de previsão do guest.
 *
 * O piso é o amortecedor: sem rede medida ainda, cobre ao menos a fila. O teto
 * existe porque a previsão avança TODOS os aviões, inclusive os aliados, e
 * aliado é extrapolado repetindo o último input conhecido — prever meio
 * segundo à frente transformaria a curva de um parceiro em correção visível.
 * 15 ticks são 250 ms, o que já cobre ida-e-volta de ~400 ms.
 */
const COOP_PREDICT_MIN = COOP_BUFFER_TICKS
const COOP_PREDICT_MAX = 15

/** Countdown da largada (§12.3): 3 s locais a partir do begin. */
const RACE_COUNTDOWN_MS = 3000
const PROGRESS_INTERVAL_MS = 100

export class GameScene extends Phaser.Scene {
  private host!: SimHost
  private sprites!: Phaser.GameObjects.Graphics
  private hud!: Phaser.GameObjects.Graphics
  private hudText!: Phaser.GameObjects.BitmapText
  private centerText!: Phaser.GameObjects.BitmapText
  private debugText!: Phaser.GameObjects.BitmapText
  /** Linha fixa com a saída para o menu — ver `create`. */
  private exitHint!: Phaser.GameObjects.BitmapText
  /** Aparelho de dedo: decide se o botão de MENU e a dica existem. */
  private touchUi = false
  private menuButton: TouchButton | null = null

  private keys!: {
    left: Phaser.Input.Keyboard.Key
    right: Phaser.Input.Keyboard.Key
    up: Phaser.Input.Keyboard.Key
    down: Phaser.Input.Keyboard.Key
    a: Phaser.Input.Keyboard.Key
    d: Phaser.Input.Keyboard.Key
    w: Phaser.Input.Keyboard.Key
    s: Phaser.Input.Keyboard.Key
    fire: Phaser.Input.Keyboard.Key
    // Tiro do P2 no co-op local. Sem tecla própria os dois aviões atiravam no
    // mesmo ESPAÇO (playtest 3). SHIFT e F ficam sob a mão que já está no
    // WASD; CTRL está descartado de propósito — Ctrl+W fecha a aba e é
    // exatamente onde essa mão mora.
    fireShift: Phaser.Input.Keyboard.Key
    fireF: Phaser.Input.Keyboard.Key
  }

  private touch!: TouchControls
  private daily: string | null = null
  private autofire = false
  private players = 1
  private lastInputs: Input[] = []
  private race: RaceConfig | null = null
  private coop: CoopConfig | null = null
  /** Host: junta os inputs dos guests. Guest: fila autoritativa. */
  private coopInputs = new CoopInputs()
  private coopTimeline = new CoopTimeline()
  private coopSender = new GuestInputSender()
  /** Guest travado esperando tick que não chega — vira aviso na tela. */
  private coopStalled = 0
  /** Estado AUTORITATIVO salvo antes de prever; null = nada previsto. */
  private coopBase: SimState | null = null
  /** Último conjunto autoritativo — a base da previsão dos aliados. */
  private coopLastAuth: Input[] = []
  private ghosts = new Map<string, GhostState>()
  private raceStartAt = 0
  private lastProgressAt = 0
  private raceOverSent = false
  /** Interferência (§12.3): última seção JÁ anunciada aos oponentes. */
  private lastInterferenceAt = 0
  private sideTexts: Phaser.GameObjects.BitmapText[] = []
  private lastPlaneState = 'alive'
  /** Recorde é gravado UMA vez por partida, na transição para game over. */
  private runRecorded = false
  private newRecord = false
  /** Pista de season desta partida, ou null. */
  private seasonTrack: number | null = null
  /** Texto do envio ao ranking; vazio enquanto não houve resposta. */
  private seasonMessage = ''
  /** Cosmético: explosões de entidades (diff vivo→morto entre frames). */
  private booms: Boom[] = []
  private entityWasAlive = new Map<string, boolean>()
  /** Cosmético: fagulhas onde balas morrem (diff do pool entre frames). */
  private sparks: Boom[] = []
  private bulletWasAlive: boolean[] = []
  private crt!: Phaser.GameObjects.Graphics
  private crtOn = false

  /**
   * Pools de Image por textura (§8: pooling — nada de criar objeto por
   * frame). acquire() reusa; endImageFrame() esconde o excedente.
   */
  private imagePools = new Map<string, Phaser.GameObjects.Image[]>()
  private imageUsed = new Map<string, number>()
  private bridgePool: Phaser.GameObjects.TileSprite[] = []
  private bridgeUsed = 0
  /**
   * Pool PRÓPRIO dos ícones de vida. Não usa `acquireImage` de propósito: lá
   * o pool é por chave de textura e a profundidade só vale na criação, então
   * reusar `player_jet` (que o avião voa em depth 4) para um ícone de HUD em
   * depth 7 entregaria o objeto com a profundidade errada.
   */
  private lifePool: Phaser.GameObjects.Image[] = []
  private lifeUsed = 0
  private river!: RiverView
  private audio!: AudioSystem

  constructor() {
    // Phaser 4 INICIA todas as cenas do array de config (≠ Phaser 3, que só
    // inicia a primeira): active:false + start explícito no postBoot.
    super({ key: 'game', active: false })
  }

  init(data: SceneData): void {
    const params = new URLSearchParams(location.search)
    // Vindo do menu, data.seed/daily mandam; ?seed= e ?daily na URL pulam o
    // menu (conveniência de dev e dos testes de fumaça).
    if (data.daily !== undefined) this.daily = data.daily
    else this.daily = params.has('daily') ? new Date().toISOString().slice(0, 10) : null
    const urlSeed = Number(params.get('seed'))
    const seed =
      data.seed ??
      (this.daily !== null
        ? dailySeed(this.daily)
        : Number.isInteger(urlSeed) && urlSeed > 0
          ? urlSeed
          : CLASSIC_SEED)
    // Modo dev de co-op local sem gamepad: ?coop=2 (setas=P1, WASD=P2).
    // O lobby real multi-gamepad chega com hardware na mesa.
    const coopParam = Number(params.get('coop'))
    this.players = data.players ?? (Number.isInteger(coopParam) ? Math.min(4, Math.max(1, coopParam)) : 1)
    this.race = data.race ?? null
    this.coop = data.coop ?? null
    this.coopInputs = new CoopInputs()
    this.coopTimeline = new CoopTimeline()
    this.coopSender = new GuestInputSender()
    this.coopStalled = 0
    this.coopBase = null
    this.coopLastAuth = []
    this.ghosts = new Map()
    this.raceOverSent = false
    this.lastInterferenceAt = 0
    this.sideTexts = []
    if (this.race !== null) {
      this.players = 1
      this.host = new SimHost(seed, 1, 'race')
      this.raceStartAt = performance.now() + RACE_COUNTDOWN_MS
      this.race.link.onMessage = (from, d) => this.onRaceMessage(from, d)
      this.race.link.onPeerLeft = () => {}
      // Elenco fechado na largada: negocia os DataChannels (§13.3). A
      // corrida COMEÇA no relay e migra por par quando o canal abre.
      this.race.link.startP2P()
    } else if (this.coop !== null) {
      // Mundo COMPARTILHADO: todo mundo simula os mesmos N aviões. É o
      // oposto da corrida, onde cada um tem o seu mundo e vê fantasmas.
      this.players = this.coop.peers.length
      this.host = new SimHost(seed, this.players, 'coopOnline')
      this.coop.link.onMessage = (from, d) => this.onCoopMessage(from, d)
      this.coop.link.startP2P()
    } else {
      this.host = new SimHost(seed, this.players)
    }
    // Autofire (§7.2): opção persistida; padrão ligado só no toque.
    this.autofire = localStorage.getItem('pixelraid:autofire') === '1'
    this.lastPlaneState = 'alive'
    this.runRecorded = false
    this.newRecord = false
    this.seasonTrack = data.seasonTrack ?? null
    this.seasonMessage = ''
    this.booms = []
    this.entityWasAlive = new Map()
    this.sparks = []
    this.bulletWasAlive = []
    // Cenas do Phaser são instâncias ÚNICAS: restart (R/revanche) e
    // start-vindo-do-menu destroem os display objects, mas os campos da
    // classe sobrevivem — pools não zerados viram referências a objetos
    // destruídos e NADA renderiza ("assets não carregam" no restart).
    this.imagePools = new Map()
    this.imageUsed = new Map()
    this.bridgePool = []
    this.bridgeUsed = 0
    this.lifePool = []
    this.lifeUsed = 0
    this.ghosts = new Map()
  }

  preload(): void {
    // Sprites aprovados no Gate D (proveniência em assets-src/art/provenance).
    this.load.setBaseURL(import.meta.env.BASE_URL)
    for (const key of [
      'player_jet',
      'enemy_heli',
      'enemy_ship',
      'fuel_depot',
      'bridge_strip',
      'enemy_jet',
      'fx_explosion',
      'deco_tree',
      'deco_house',
    ]) {
      this.load.image(key, `assets/sprites/${key}.png`)
    }
    this.load.image('tileset_river', 'assets/sprites/tileset_river.png')
    // Tiras de animação (5 frames de 32×32 lado a lado; frame 0 = sprite base).
    this.load.spritesheet('enemy_heli_anim', 'assets/sprites/enemy_heli_anim.png', {
      frameWidth: 32,
      frameHeight: 32,
    })
    this.load.spritesheet('fx_explosion_anim', 'assets/sprites/fx_explosion_anim.png', {
      frameWidth: 32,
      frameHeight: 32,
    })
    AudioSystem.preload(this.load)
    preloadPixelFonts(this)
  }

  create(): void {
    // O cenário (água, margens Wang e decoração) é do RiverView — o mesmo
    // objeto que o menu usa de pano de fundo. Aqui cobre só a área de jogo.
    this.river = new RiverView(this, {
      seed: this.host.seed,
      spanAt: (line) => this.host.sim.terrainSpanAtLine(line),
      height: SCREEN.PLAYFIELD_HEIGHT,
    })

    // Ícone de vida: o avião ocupa 23×24 dentro do arquivo de 32×32, mas o
    // recorte é 24×24 — metade de 23 seria 11,5, e meio pixel em arte de
    // pixel é a origem do borrão. A coluna transparente a mais não aparece.
    const jet = this.textures.get('player_jet')
    if (jet.has('life') === false) jet.add('life', 0, 3, 2, 24, 24)

    this.sprites = this.add.graphics().setDepth(3)
    this.hud = this.add.graphics().setDepth(6)
    this.hudText = pixelText(this, 4, SCREEN.PLAYFIELD_HEIGHT + 34, 10, '', {
      color: COLORS.hudText,
      depth: 7,
    })
    this.centerText = pixelText(this, SCREEN.WIDTH / 2, 140, 16, '', {
      color: '#ffffff',
      align: 'center',
      origin: [0.5, 0.5],
      depth: 8,
    })
    // Sair para o menu já existia no ESC, mas em lugar nenhum da tela — atalho
    // que só quem leu o código conhece não é uma saída, é uma senha. A linha
    // muda com o modo porque R significa coisas diferentes em cada um.
    this.exitHint = pixelText(this, 4, SCREEN.PLAYFIELD_HEIGHT + 48, 10, '', {
      color: '#5d7183',
      depth: 7,
    })
    // ── Toque ──────────────────────────────────────────────────────────────
    // O manche por arrasto já existia, mas era INVISÍVEL e a única saída da
    // partida era o ESC — tecla que um celular não tem. Sem estes dois, o jogo
    // no telefone era jogável e não era operável.
    this.menuButton = null
    this.touchUi = isTouchDevice(this)
    if (this.touchUi) {
      this.menuButton = new TouchButton(
        this,
        SCREEN.WIDTH - 32,
        SCREEN.PLAYFIELD_HEIGHT + 46,
        56,
        22,
        'MENU',
        () => this.voltarAoMenu(),
        { size: 10, color: '#9ae6b4', depth: 8 },
      )
      this.montarDicaDeToque()
    }

    this.debugText = pixelText(this, 2, 2, 8, '', { color: '#7fd4ff', depth: 9 })
    this.debugText.setVisible(new URLSearchParams(location.search).has('debug'))

    const keyboard = this.input.keyboard!
    const K = Phaser.Input.Keyboard.KeyCodes
    this.keys = {
      left: keyboard.addKey(K.LEFT),
      right: keyboard.addKey(K.RIGHT),
      up: keyboard.addKey(K.UP),
      down: keyboard.addKey(K.DOWN),
      a: keyboard.addKey(K.A),
      d: keyboard.addKey(K.D),
      w: keyboard.addKey(K.W),
      s: keyboard.addKey(K.S),
      fire: keyboard.addKey(K.SPACE),
      fireShift: keyboard.addKey(K.SHIFT),
      fireF: keyboard.addKey(K.F),
    }

    // R/N/ESC por EVENTO: no Phaser 4, JustDown com objetos Key não dispara
    // (≠ P3); keyboard.on('keydown-X') é o caminho provado pelo menu/lobby.
    onKey(keyboard, 'keydown-R', () => {
      if (this.race !== null) {
        // Na corrida, R = REVANCHE (só host, só no pódio): seed nova, mesma
        // sala — os DataChannels sobrevivem ao restart (§12.3).
        if (this.race.mySlot === 0 && this.raceFinished()) {
          const seed = (Math.random() * 0xffffffff) >>> 0
          this.race.link.send({ k: 'rematch', seed })
          this.scene.restart({ seed, daily: null, race: this.race } satisfies SceneData)
        }
        return
      }
      this.scene.restart({ seed: this.host.seed } satisfies SceneData)
    })
    onKey(keyboard, 'keydown-N', () => {
      if (this.race !== null) return
      // Escolha de seed é meta-jogo, não simulação: aleatório real é bem-vindo.
      this.scene.restart({ seed: (Math.random() * 0xffffffff) >>> 0, daily: null } satisfies SceneData)
    })
    onKey(keyboard, 'keydown-ESC', () => this.voltarAoMenu())

    this.touch = new TouchControls(this)
    this.audio = new AudioSystem(this)
    this.audio.bindKeys(this.input.keyboard!)
    // Restart/saída: o loop do motor é do SoundManager GLOBAL — sem isto,
    // cada R empilha um motor tocando por cima do outro.
    this.events.once('shutdown', () => this.audio.destroy())

    // Modo CRT (§16): scanlines estáticas por cima de tudo. Toggle C,
    // persistido com o prefixo de localStorage do contrato pf-save (§19).
    this.crt = this.add.graphics().setDepth(10)
    this.crt.fillStyle(0x000000, 0.18)
    for (let y = 0; y < SCREEN.HEIGHT; y += 2) this.crt.fillRect(0, y, SCREEN.WIDTH, 1)
    this.crt.fillStyle(0x000000, 0.25)
    this.crt.fillRect(0, 0, 2, SCREEN.HEIGHT)
    this.crt.fillRect(SCREEN.WIDTH - 2, 0, 2, SCREEN.HEIGHT)
    this.crtOn = localStorage.getItem('pixelraid:crt') === '1'
    this.crt.setVisible(this.crtOn)
    onKey(this.input.keyboard!, 'keydown-C', () => {
      this.crtOn = !this.crtOn
      this.crt.setVisible(this.crtOn)
      localStorage.setItem('pixelraid:crt', this.crtOn ? '1' : '0')
    })

    // Gancho de inspeção: teste de fumaça e depuração manual leem daqui.
    window.__pixelraid = {
      seed: this.host.seed,
      tick: () => this.host.sim.tick,
      // Tick AUTORITATIVO: no guest, `tick()` pode estar adiantado pela
      // previsão local. Medir sincronia pelo previsto seria medir o palpite.
      authTick: () => this.coopBase?.tick ?? this.host.sim.tick,
      checksum: () => this.host.sim.checksum(),
      gameOver: () => this.host.sim.gameOver,
      distancePx: () => this.host.sim.distancePx,
      sections: () => this.host.sim.sectionsCleared,
      planeX: () => fpToPx(this.host.sim.planes[0]?.x ?? 0),
      terrainSpan: (line: number) => this.host.sim.terrainSpanAtLine(line),
      ghosts: () => this.ghosts.size,
      transport: () => this.race?.link.transport() ?? null,
      replay: () => this.host.exportReplay(),
    }
  }

  override update(_time: number, delta: number): void {
    // Toque no game over reinicia (R/N/ESC são handlers de evento no create).
    const tapped = this.touch.consumeTap()
    if (this.race === null && tapped && this.host.sim.gameOver) {
      this.scene.restart({ seed: this.host.seed } satisfies SceneData)
      return
    }

    this.lastInputs = this.readInputs()
    // Largada sincronizada (§12.3): ninguém anda antes do fim do countdown.
    const racing = this.race !== null
    const beforeStart = racing && performance.now() < this.raceStartAt
    if (this.coop !== null) this.coopTick(delta)
    else if (!beforeStart) this.host.update(delta, this.lastInputs)
    if (racing) this.raceTick()
    // Áudio e efeitos leem o estado AUTORITATIVO, antes da previsão. Se
    // lessem o previsto, um inimigo poderia morrer na previsão, voltar vivo
    // no rewind e morrer de novo — duas explosões para uma morte.
    this.audio.update(this.host.sim)
    this.reactToStateChanges()
    this.coopPredict()
    this.render()
  }

  /** Mensagens de corrida vindas do relay (relay cego: só nós entendemos `d`). */
  private onRaceMessage(from: string, d: Record<string, unknown>): void {
    if (d.k === 'progress') {
      this.ghosts.set(from, {
        slot: Number(d.slot),
        distPx: Number(d.dist),
        x: Number(d.x),
        fuel01: Number(d.fuel01),
        alive: d.alive === true,
        score: Number(d.score),
      })
      return
    }
    // Interferência (§12.3): um oponente derrubou ponte ⇒ 1 jato extra para
    // MIM (se estou vivo). Entra como entidade externa com ID próprio.
    if (d.k === 'incoming_jet') {
      if (this.host.sim.planes[0]?.state === 'alive') this.host.sim.injectJet()
      return
    }
    // Revanche (§12.3): host reinicia todos com seed nova, mesma sala.
    if (d.k === 'rematch' && this.race !== null) {
      this.scene.restart({ seed: Number(d.seed), daily: null, race: this.race } satisfies SceneData)
    }
  }

  /** Envio de progresso a 10 Hz (§13.4) e detecção de fim de corrida. */
  private raceTick(): void {
    const race = this.race
    if (race === null) return
    const sim = this.host.sim
    // Derrubei uma ponte? Aviso os oponentes — 1 jato extra para cada (§12.3).
    if (sim.sectionsCleared > this.lastInterferenceAt) {
      this.lastInterferenceAt = sim.sectionsCleared
      race.link.send({ k: 'incoming_jet' })
    }

    const now = performance.now()
    if (now - this.lastProgressAt >= PROGRESS_INTERVAL_MS) {
      this.lastProgressAt = now
      const plane = sim.planes[0]!
      race.link.send({
        k: 'progress',
        slot: race.mySlot,
        dist: sim.distancePx,
        x: fpToPx(plane.x),
        fuel01: Math.max(0, Math.round((plane.fuel / FUEL.TANK) * 100) / 100),
        alive: plane.state !== 'gone',
        score: sim.totalScore,
      })
    }
  }

  /**
   * Um tick de co-op online. Host e guest fazem coisas OPOSTAS aqui: o host
   * decide o tempo e publica; o guest obedece ao que chega.
   */
  private coopTick(delta: number): void {
    const coop = this.coop
    if (coop === null) return

    if (coop.isHost) {
      // O host é o relógio (§13.1). Ele junta os inputs recebidos, avança e
      // publica UM pacote por tick simulado — o guest precisa de todos, em
      // ordem, ou trava de propósito.
      const antes = this.host.sim.tick
      const inputs = this.coopInputs.forTick(this.lastInputs[0] ?? NEUTRAL_INPUT, this.players, antes)
      const steps = this.host.update(delta, inputs)
      for (let i = 0; i < steps; i++) {
        coop.link.send({ k: 'ct', ...CoopTimeline.publish(antes + i, inputs) })
      }
      return
    }

    // Desfaz a previsão do frame anterior. Ela é enfeite de renderização: o
    // estado de onde os ticks autoritativos partem tem de ser o do HOST, não
    // o que este cliente imaginou.
    if (this.coopBase !== null) {
      this.host.sim.restore(this.coopBase)
      this.coopBase = null
    }

    // Guest: manda o próprio input (só quando muda) e simula o que chegou.
    const envio = this.coopSender.next(this.lastInputs[0] ?? NEUTRAL_INPUT)
    if (envio !== null) coop.link.send({ k: 'ci', slot: coop.mySlot, ...envio })

    // Teto de ticks por frame: sem ele, um lote atrasado seria consumido de
    // uma vez e o jogo daria um salto em vez de recuperar suavemente.
    let simulados = 0
    for (;;) {
      if (simulados >= 8) break
      const inputs = this.coopTimeline.next()
      if (inputs === null) break
      this.host.stepAuthoritative(inputs)
      this.coopLastAuth = inputs
      simulados++
    }
    // Nada para simular é normal por um frame ou dois; muito tempo assim é
    // conexão quebrada, e o jogador merece saber em vez de ver a tela parada.
    this.coopStalled = simulados > 0 ? 0 : this.coopStalled + 1
  }

  /**
   * Previsão local do guest (§13.6): avança alguns ticks à frente do último
   * autoritativo, só para DESENHAR, e desfaz no frame seguinte.
   *
   * Sem isto o avião do guest responde com o atraso de ida-e-volta: ele
   * pressiona, o host simula, o pacote volta, e só então a nave anda. Com
   * isto, o próprio avião obedece na hora.
   *
   * A previsão usa o `capture`/`restore` do CORE, e não uma cópia da regra de
   * movimento no cliente — de propósito. Reescrever aqui como o avião anda
   * seria criar uma segunda física, que diverge da primeira no dia em que
   * alguém ajustar a aceleração no config.
   *
   * Os ALIADOS são previstos repetindo o último input conhecido deles, não
   * com input neutro: quem estava acelerando continua acelerando. É o mesmo
   * princípio do `STALE_TICKS` no host, e evita que o parceiro pareça parar e
   * saltar a cada frame.
   */
  private coopPredict(): void {
    const coop = this.coop
    if (coop === null || coop.isHost || this.host.sim.gameOver) return
    if (this.coopLastAuth.length === 0) return

    this.coopBase = this.host.sim.capture()
    const meu = this.lastInputs[0] ?? NEUTRAL_INPUT
    const inputs = [...this.coopLastAuth]
    inputs[coop.mySlot] = meu
    for (let i = 0; i < this.coopPredictTicks(); i++) this.host.sim.step(inputs)
  }

  /** Profundidade da previsão, medida pelo RTT — o porquê está em `net/coop.ts`. */
  private coopPredictTicks(): number {
    return predictTicks(this.coop?.link.rttToHost() ?? null, {
      bufferTicks: COOP_BUFFER_TICKS,
      tickMs: TICK_MS,
      min: COOP_PREDICT_MIN,
      max: COOP_PREDICT_MAX,
    })
  }

  private onCoopMessage(from: string, d: Record<string, unknown>): void {
    const coop = this.coop
    if (coop === null) return
    if (coop.isHost && d.k === 'ci') {
      // Só o host lê input alheio; guest ignora, porque não é autoridade.
      this.coopInputs.receive(
        Number(d.slot),
        Number(d.seq),
        Number(d.b),
        this.host.sim.tick,
      )
      return
    }
    if (!coop.isHost && d.k === 'ct') {
      this.coopTimeline.receive({ t: Number(d.t), b: (d.b as number[]) ?? [] })
    }
    void from
  }

  /** Corrida acabou para TODOS? (eu morto + todo fantasma morto) */
  private raceFinished(): boolean {
    if (!this.host.sim.gameOver) return false
    for (const ghost of this.ghosts.values()) if (ghost.alive) return false
    return true
  }

  /** Classificação por distância (§12.3); desempate por score. */
  private racePodium(): string {
    const rows: { slot: number; dist: number; score: number; me: boolean }[] = [
      { slot: this.race!.mySlot, dist: this.host.sim.distancePx, score: this.host.sim.totalScore, me: true },
    ]
    for (const ghost of this.ghosts.values()) {
      rows.push({ slot: ghost.slot, dist: ghost.distPx, score: ghost.score, me: false })
    }
    rows.sort((a, b) => b.dist - a.dist || b.score - a.score)
    return rows
      .map((row, index) => `${index + 1}. P${row.slot + 1}${row.me ? ' (você)' : ''}  ${row.dist}m`)
      .join('\n')
  }

  /**
   * Volta ao menu. Chamada pelo ESC e pelo botão de toque — o mesmo caminho,
   * porque saída que existe só para quem tem teclado não é saída.
   *
   * Fecha o link dos DOIS modos online. Antes só a corrida era fechada, e sair
   * de um co-op deixava o socket vivo: o host seguia esperando input de um
   * avião que já não estava lá.
   */
  private voltarAoMenu(): void {
    this.race?.link.close()
    this.coop?.link.close()
    this.scene.start('menu')
  }

  /**
   * Dica das zonas de toque, uma vez por jogador.
   *
   * O manche é a metade ESQUERDA (arrasto relativo) e o tiro é a metade
   * DIREITA — um esquema que não rouba espaço da tela, mas que ninguém
   * adivinha. Some no primeiro toque e não volta: `pixelraid:touch-visto`
   * (o prefixo já sincroniza na nuvem, então quem trocar de aparelho não
   * leva a dica de novo).
   */
  private montarDicaDeToque(): void {
    if (localStorage.getItem('pixelraid:touch-visto') === '1') return

    const meioY = SCREEN.PLAYFIELD_HEIGHT / 2
    const veu = this.add.graphics().setDepth(30)
    veu.fillStyle(0x000814, 0.72)
    veu.fillRect(0, 0, SCREEN.WIDTH, SCREEN.PLAYFIELD_HEIGHT)
    veu.lineStyle(1, 0x5d7183, 0.9)
    veu.beginPath()
    veu.moveTo(SCREEN.WIDTH / 2, 0)
    veu.lineTo(SCREEN.WIDTH / 2, SCREEN.PLAYFIELD_HEIGHT)
    veu.strokePath()

    const textos = [
      pixelText(this, 64, meioY - 12, 12, 'ARRASTE\nAQUI', {
        color: '#9ae6b4',
        align: 'center',
        origin: [0.5, 0.5],
        depth: 31,
      }),
      pixelText(this, 192, meioY - 12, 12, 'TOQUE\nPARA ATIRAR', {
        color: '#ffd23c',
        align: 'center',
        origin: [0.5, 0.5],
        depth: 31,
      }),
      pixelText(this, SCREEN.WIDTH / 2, meioY + 60, 10, 'toque para começar', {
        color: '#f2f2f2',
        align: 'center',
        origin: [0.5, 0.5],
        depth: 31,
      }),
    ]

    // `once` e não `on`: a dica sai no primeiro toque, e esse mesmo toque não
    // deve também atirar — por isso ela vive acima de tudo e só solta a tela
    // depois de sumir.
    this.input.once('pointerdown', () => {
      localStorage.setItem('pixelraid:touch-visto', '1')
      veu.destroy()
      for (const t of textos) t.destroy()
    })
  }

  private readInputs(): Input[] {
    const touch = this.touch.read()
    const arrows: Input = {
      moveX: this.keys.left.isDown === this.keys.right.isDown ? 0 : this.keys.left.isDown ? -1 : 1,
      thrust: this.keys.up.isDown === this.keys.down.isDown ? 0 : this.keys.up.isDown ? 1 : -1,
      fire: this.keys.fire.isDown,
    }
    const wasd: Input = {
      moveX: this.keys.a.isDown === this.keys.d.isDown ? 0 : this.keys.a.isDown ? -1 : 1,
      thrust: this.keys.w.isDown === this.keys.s.isDown ? 0 : this.keys.w.isDown ? 1 : -1,
      fire: this.keys.fireShift.isDown || this.keys.fireF.isDown,
    }

    /**
     * Dividir setas/WASD só faz sentido quando DUAS pessoas dividem um
     * teclado — o co-op local. Nos modos online cada navegador pilota um
     * avião só, e a divisão deixava metade do teclado morta: quem jogava de
     * WASD numa corrida ou num co-op online não movia nada, sem erro nenhum
     * na tela para explicar por quê.
     */
    const tecladoDividido = this.players > 1 && this.coop === null

    if (!tecladoDividido) {
      // Um piloto neste teclado: setas e WASD são o MESMO jogador (§15), mais
      // toque e autofire. Os dois gatilhos valem, para quem joga de WASD não
      // ter de esticar até o ESPAÇO.
      const meu: Input = {
        moveX: arrows.moveX !== 0 ? arrows.moveX : wasd.moveX !== 0 ? wasd.moveX : touch.moveX,
        thrust: arrows.thrust !== 0 ? arrows.thrust : wasd.thrust !== 0 ? wasd.thrust : touch.thrust,
        fire: this.autofire || arrows.fire || wasd.fire || touch.fire,
      }
      // Índice 0 é sempre o jogador LOCAL, inclusive online: quem traduz para
      // o slot da sala é o `coopTick`/`coopPredict`.
      const inputs: Input[] = [meu]
      while (inputs.length < this.players) inputs.push(NEUTRAL_INPUT)
      return inputs
    }

    // Co-op local: P1 = setas+toque+ESPAÇO, P2 = WASD+SHIFT/F. P3/P4 aguardam
    // gamepads — voam neutros.
    const inputs: Input[] = [
      {
        moveX: arrows.moveX !== 0 ? arrows.moveX : touch.moveX,
        thrust: arrows.thrust !== 0 ? arrows.thrust : touch.thrust,
        fire: this.autofire || arrows.fire || touch.fire,
      },
      { ...wasd, fire: this.autofire || wasd.fire },
    ]
    while (inputs.length < this.players) inputs.push(NEUTRAL_INPUT)
    return inputs
  }

  /** Feedback de morte (§7.3): shake + flash. Cosmético, fora da simulação. */
  private reactToStateChanges(): void {
    const state = this.host.sim.planes[0]?.state ?? 'gone'
    if (state === 'exploding' && this.lastPlaneState === 'alive') {
      this.cameras.main.shake(150, 0.012)
      this.cameras.main.flash(90, 255, 96, 32)
    }
    this.lastPlaneState = state
  }

  private acquireImage(key: string, depth: number): Phaser.GameObjects.Image {
    let pool = this.imagePools.get(key)
    if (pool === undefined) {
      pool = []
      this.imagePools.set(key, pool)
    }
    const used = this.imageUsed.get(key) ?? 0
    this.imageUsed.set(key, used + 1)
    if (used < pool.length) {
      const image = pool[used]!
      image.setVisible(true)
      image.setScale(1)
      image.setAlpha(1)
      image.setAngle(0)
      image.setFlipX(false)
      image.clearTint()
      return image
    }
    const image = this.add.image(0, 0, key).setDepth(depth)
    pool.push(image)
    return image
  }

  private acquireBridge(): Phaser.GameObjects.TileSprite {
    if (this.bridgeUsed < this.bridgePool.length) {
      const tile = this.bridgePool[this.bridgeUsed++]!
      tile.setVisible(true)
      return tile
    }
    // A faixa de treliça (64×32) repete horizontalmente; mostramos a banda
    // central de 16 px — pixel art nunca é esticada, é repetida.
    const tile = this.add.tileSprite(0, 0, 64, 16, 'bridge_strip').setDepth(2)
    tile.tilePositionY = 8
    this.bridgePool.push(tile)
    this.bridgeUsed++
    return tile
  }

  private beginImageFrame(): void {
    for (const key of this.imagePools.keys()) this.imageUsed.set(key, 0)
    this.bridgeUsed = 0
    this.lifeUsed = 0
  }

  /**
   * Ícone de vida: o próprio avião, recortado e em metade da escala.
   *
   * O recorte é 24×24 e não o 23×24 que o avião de fato ocupa: metade de 23 é
   * 11,5, e meio pixel em arte de pixel é a origem do borrão. Com 24 a metade
   * é exata, e a coluna transparente a mais não aparece.
   */
  private acquireLife(): Phaser.GameObjects.Image {
    if (this.lifeUsed < this.lifePool.length) {
      const image = this.lifePool[this.lifeUsed++]!
      image.setVisible(true)
      return image
    }
    const image = this.add
      .image(0, 0, 'player_jet', 'life')
      .setOrigin(0, 0)
      .setScale(0.5)
      .setDepth(7)
    this.lifePool.push(image)
    this.lifeUsed++
    return image
  }

  private endImageFrame(): void {
    for (const [key, pool] of this.imagePools) {
      const used = this.imageUsed.get(key) ?? 0
      for (let i = used; i < pool.length; i++) pool[i]!.setVisible(false)
    }
    for (let i = this.bridgeUsed; i < this.bridgePool.length; i++) this.bridgePool[i]!.setVisible(false)
    for (let i = this.lifeUsed; i < this.lifePool.length; i++) this.lifePool[i]!.setVisible(false)
  }

  private render(): void {
    const sim = this.host.sim
    const scrollPx = sim.distancePx
    this.beginImageFrame()

    this.river.draw(scrollPx)

    // ── Avião / explosão ──
    this.sprites.clear()

    // Entidades: o diff vivo→morto entre frames vira explosão cosmética.
    const seen = new Map<string, boolean>()
    for (const entity of sim.entities) {
      const key = `${entity.chunk}:${entity.slot}`
      seen.set(key, entity.alive)
      const ex = fpToPx(entity.x)
      const eyWorld = entity.line * SCREEN.TILE + SCREEN.TILE / 2
      const ey = SCREEN.PLAYFIELD_HEIGHT - (eyWorld - scrollPx)
      if (!entity.alive) {
        if (this.entityWasAlive.get(key) === true) this.booms.push({ x: ex, y: ey, ticks: 14 })
        continue
      }
      if (ey < -24 || ey > SCREEN.PLAYFIELD_HEIGHT + 24) continue

      if (entity.type === 'bridge') {
        // Barreira sobre todo o rio (§8): treliça repetida + torres.
        const span = sim.terrainSpanAtLine(entity.line)
        const tile = this.acquireBridge()
        tile.setPosition((span.leftPx + span.rightPx) / 2, ey)
        tile.width = span.rightPx - span.leftPx
        this.sprites.fillStyle(COLORS.bridgeTower)
        this.sprites.fillRect(span.leftPx - 6, ey - 12, 8, 24)
        this.sprites.fillRect(span.rightPx - 2, ey - 12, 8, 24)
        continue
      }
      if (entity.type === 'jet') {
        if (entity.waitTicks > 0) {
          // Aviso de 300 ms (§8): alerta piscando na borda de entrada.
          if (sim.tick % 4 < 2) {
            this.sprites.fillStyle(COLORS.jetWarning)
            const wx = entity.dir === 1 ? 2 : SCREEN.WIDTH - 6
            this.sprites.fillRect(wx, ey - 6, 4, 12)
          }
          continue
        }
        const image = this.acquireImage('enemy_jet', 2)
        image.setPosition(ex, ey)
        image.setFlipX(entity.dir === -1)
        continue
      }
      if (entity.type === 'ship') {
        const image = this.acquireImage('enemy_ship', 2)
        image.setPosition(ex, ey)
        image.setFlipX(entity.dir === -1)
      } else if (entity.type === 'heli') {
        // Rotor de verdade: 5 frames do animate_image ciclando a ~15 Hz.
        const image = this.acquireImage('enemy_heli_anim', 2)
        image.setFrame(((sim.tick >> 2) % 5))
        image.setPosition(ex, ey)
        image.setFlipX(entity.dir === -1)
      } else {
        const image = this.acquireImage('fuel_depot', 2)
        image.setPosition(ex, ey)
        image.setAngle(0)
      }
    }
    this.entityWasAlive = seen

    // Balas — na cor do dono (§7.5) — e fagulha onde uma morreu (§7.2).
    for (let i = 0; i < sim.bullets.length; i++) {
      const bullet = sim.bullets[i]!
      this.sprites.fillStyle(this.players > 1 ? slotColor(bullet.ownerSlot) : COLORS.bullet)
      if (!bullet.alive && this.bulletWasAlive[i] === true) {
        const sx = fpToPx(bullet.x)
        const sy = SCREEN.PLAYFIELD_HEIGHT - (fpToPx(bullet.worldY) - scrollPx)
        if (sy > -8 && sy < SCREEN.PLAYFIELD_HEIGHT + 8) this.sparks.push({ x: sx, y: sy, ticks: 6 })
      }
      this.bulletWasAlive[i] = bullet.alive
      if (!bullet.alive) continue
      const bx = fpToPx(bullet.x)
      const byWorld = fpToPx(bullet.worldY)
      const by = SCREEN.PLAYFIELD_HEIGHT - (byWorld - scrollPx)
      this.sprites.fillRect(bx - 2, by - 4, 4, 8)
    }

    // Fagulhas: cruz branca que encolhe.
    this.sparks = this.sparks.filter((spark) => --spark.ticks > 0)
    this.sprites.fillStyle(0xffffff)
    for (const spark of this.sparks) {
      const r = Math.max(1, spark.ticks - 2)
      this.sprites.fillRect(spark.x - r, spark.y - 1, r * 2, 2)
      this.sprites.fillRect(spark.x - 1, spark.y - r, 2, r * 2)
    }

    // Explosões cosméticas de entidades: os 5 frames animados pela fase.
    this.booms = this.booms.filter((boom) => --boom.ticks > 0)
    for (const boom of this.booms) {
      const image = this.acquireImage('fx_explosion_anim', 3)
      const phase = Math.min(4, ((14 - boom.ticks) / 3) | 0)
      image.setFrame(phase)
      image.setPosition(boom.x, boom.y)
    }

    // Aviões: um sprite por slot, tintado pela cor do time (§7.5 — o corpo
    // branco do P1 aceita tint limpo; P1 fica sem tint).
    const speedRatio = (sim.scrollSpeed - SCROLL.CRUISE_FP) / SCROLL.CRUISE_FP
    const baseY = PLAYER.BASE_Y - Math.round(speedRatio * 8)
    for (let slot = 0; slot < sim.planes.length; slot++) {
      const plane = sim.planes[slot]!
      const px = fpToPx(plane.x)
      const py = baseY - fpToPx(plane.yOffset)

      if (plane.state === 'alive') {
        const blinkOff = plane.invulnTicks > 0 && sim.tick % 8 < 4
        if (blinkOff) continue
        const image = this.acquireImage('player_jet', 4)
        image.setPosition(px, py)
        image.setTint(slotColor(slot))
        image.setAngle((this.lastInputs[slot]?.moveX ?? 0) * 6)
      } else if (plane.state === 'exploding') {
        // 24 ticks de explosão mapeados nos 5 frames (§7.3).
        const growth = PLAYER.EXPLOSION_TICKS - plane.stateTicks
        const image = this.acquireImage('fx_explosion_anim', 4)
        image.setFrame(Math.min(4, (growth / 5) | 0))
        image.setPosition(px, py)
        image.setScale(1.2)
      }
    }
    const plane = sim.planes[0]

    // ── HUD (painel inferior de 64 px, §16) ──
    this.hud.clear()
    this.hud.fillStyle(COLORS.hudPanel)
    this.hud.fillRect(0, SCREEN.PLAYFIELD_HEIGHT, SCREEN.WIDTH, SCREEN.HUD_HEIGHT)

    if (this.players > 1) {
      // HUD co-op (§16): mini-medidor por avião na cor do slot + pool do time.
      for (let slot = 0; slot < sim.planes.length; slot++) {
        const member = sim.planes[slot]!
        const x = 8 + slot * 56
        const y = SCREEN.PLAYFIELD_HEIGHT + 10
        this.hud.fillStyle(COLORS.barBack)
        this.hud.fillRect(x, y, 48, 8)
        const alive = member.state === 'alive' || member.state === 'respawning'
        this.hud.fillStyle(alive ? slotColor(slot) : 0x555555)
        const ratio = Math.max(0, member.fuel / FUEL.TANK)
        this.hud.fillRect(x + 1, y + 1, Math.round(46 * ratio), 6)
      }
      // Vidas do POOL (§16): são do TIME, então ficam sem tint de slot —
      // pintar de uma cor sugeriria que a vida pertence àquele jogador.
      for (let i = 0; i < Math.min(sim.teamLives, LIVES.COOP_POOL_MAX); i++) {
        this.acquireLife().setPosition(8 + i * 12, SCREEN.PLAYFIELD_HEIGHT + 20).clearTint()
      }
    }

    const fuel = plane?.fuel ?? 0
    const fuelRatio = fuel / FUEL.TANK
    const barX = 34
    const barW = 120
    if (this.players === 1) {
      this.hud.fillStyle(COLORS.barBack)
      this.hud.fillRect(barX, SCREEN.PLAYFIELD_HEIGHT + 10, barW, 12)
    }
    const lowFuel = fuel < FUEL.LOW_WARNING
    const flashOff = lowFuel && sim.tick % 30 < 15
    const refueling = plane?.refueling ?? false
    // Enchendo: a barra clareia piscando — o "ding" de tanque cheio vem no M4.
    this.hud.fillStyle(
      refueling && sim.tick % 10 < 5 ? COLORS.fuelFilling : lowFuel ? COLORS.fuelLow : COLORS.fuelOk,
    )
    if (this.players === 1 && !flashOff) {
      this.hud.fillRect(barX + 1, SCREEN.PLAYFIELD_HEIGHT + 11, Math.round((barW - 2) * fuelRatio), 10)
    }
    if (this.players === 1) {
      // Marcas E · ½ · F
      this.hud.fillStyle(0xffffff)
      for (const mark of [0, 0.5, 1]) {
        this.hud.fillRect(barX + Math.round((barW - 2) * mark), SCREEN.PLAYFIELD_HEIGHT + 23, 1, 3)
      }
      // Vidas: a ativa não conta como reserva (H-07).
      //
      // Começa em 180 e não em 200 como antes: com SOLO_MAX_RESERVE = 6 e
      // passo 12, o antigo punha o 6º ícone em x=260 — fora dos 256 px da
      // tela. Só aparecia com o pool cheio, por isso ninguém tinha visto.
      const reserves = Math.max(0, (plane?.lives ?? 0) - (plane?.state === 'gone' ? 0 : 1))
      for (let i = 0; i < Math.min(reserves, LIVES.SOLO_MAX_RESERVE); i++) {
        this.acquireLife().setPosition(180 + i * 12, SCREEN.PLAYFIELD_HEIGHT + 10).clearTint()
      }
    }

    const dist = String(sim.distancePx).padStart(6, '0')
    const score = String(sim.totalScore % 1_000_000).padStart(6, '0')
    const scoreText = sim.totalScore >= 1_000_000 ? '!!!!!!' : score
    const section = String(sim.sectionsCleared).padStart(2, '0')
    const dailyTag = this.daily !== null ? ' D' : ''
    // Corrida: etiqueta do transporte (§13.3 — P2P verde-intenção/RELAY).
    const netTag = this.race !== null ? `  ${this.race.link.transport().label}` : ''
    this.hudText.setText(`${scoreText}   DIST ${dist}m   S${section}${dailyTag}${netTag}`)
    setPixelText(
      this.exitHint,
      this.touchUi
        ? // 170px: o botão MENU ocupa de 196 a 252, e esta linha começa em
          // x=4. A versão longa media 220px e passava POR BAIXO do botão —
          // a palavra "atira" sumia atrás dele.
          'esquerda arrasta · direita atira'
        : this.coop !== null
          ? 'ESC sai da sala'
          : this.race !== null
            ? 'ESC sai da sala · R revanche'
            : 'ESC menu · R reinicia · N nova seed',
    )

    // ── Overlays ──
    if (this.race === null) {
      if (sim.gameOver) {
        // Grava na TRANSIÇÃO, não a cada frame: render roda 60x por segundo e
        // o Diário aceita só a primeira tentativa do dia.
        if (!this.runRecorded) {
          this.runRecorded = true
          this.newRecord = recordRun(
            { score: sim.totalScore, distPx: sim.distancePx, sections: sim.sectionsCleared },
            { daily: this.daily, isClassic: this.daily === null && this.host.seed === CLASSIC_SEED },
          )
          // Envio ao ranking: só em pista de season, e sem bloquear nada. O
          // servidor reexecuta o replay e decide o score — o que mandamos é o
          // log de inputs, não uma alegação.
          if (this.seasonTrack !== null) {
            this.seasonMessage = 'enviando ao ranking...'
            void submitRun(this.host.exportReplay()).then((r) => {
              if (this.scene.isActive()) this.seasonMessage = submitMessage(r)
            })
          }
        }
        const replaySize = this.host.exportReplay().runs.length * 4
        setPixelSize(this.centerText, 16)
        setPixelText(
          this.centerText,
          `GAME OVER\n\nSCORE ${sim.totalScore}\nDIST ${sim.distancePx}m\nreplay ${replaySize}B\n\nR ou toque: reinicia\nN: nova seed`,
        )
      } else {
        setPixelText(this.centerText, '')
      }
    }

    // ── Corrida (§12.3): fantasmas, countdown e placar lateral ──
    if (this.race !== null) {
      const myDist = sim.distancePx
      for (const ghost of this.ghosts.values()) {
        if (!ghost.alive) continue
        const delta = ghost.distPx - myDist
        const plane0 = sim.planes[0]!
        const myY = PLAYER.BASE_Y - fpToPx(plane0.yOffset)
        const ghostY = myY - Math.max(-SCREEN.PLAYFIELD_HEIGHT, Math.min(SCREEN.PLAYFIELD_HEIGHT, delta))
        if (ghostY > -16 && ghostY < SCREEN.PLAYFIELD_HEIGHT + 16) {
          const image = this.acquireImage('player_jet', 4)
          image.setPosition(ghost.x, ghostY)
          image.setTint(slotColor(ghost.slot))
          image.setAlpha(0.35)
        } else {
          // Fora da tela: seta na borda apontando onde o oponente está (§12.3).
          this.sprites.fillStyle(slotColor(ghost.slot))
          const arrowY = ghostY <= 0 ? 6 : SCREEN.PLAYFIELD_HEIGHT - 6
          this.sprites.fillTriangle(
            ghost.x - 4,
            arrowY + (ghostY <= 0 ? 4 : -4),
            ghost.x + 4,
            arrowY + (ghostY <= 0 ? 4 : -4),
            ghost.x,
            arrowY,
          )
        }
      }

      // Placar lateral (§16): coluna direita com marcador de progresso e
      // score por slot — líder no topo.
      this.sprites.fillStyle(0x000000, 0.35)
      this.sprites.fillRect(SCREEN.WIDTH - 20, 0, 20, SCREEN.PLAYFIELD_HEIGHT)
      const standings: { slot: number; dist: number; score: number; alive: boolean }[] = [
        {
          slot: this.race.mySlot,
          dist: myDist,
          score: sim.totalScore,
          alive: sim.planes[0]!.state !== 'gone',
        },
      ]
      for (const ghost of this.ghosts.values()) {
        standings.push({ slot: ghost.slot, dist: ghost.distPx, score: ghost.score, alive: ghost.alive })
      }
      standings.sort((a, b) => b.dist - a.dist)
      const leaderDist = standings[0]?.dist ?? 0
      while (this.sideTexts.length < standings.length) {
        this.sideTexts.push(pixelText(this, 0, 0, 8, '', { color: '#ffffff', depth: 5 }))
      }
      for (let i = 0; i < standings.length; i++) {
        const row = standings[i]!
        // Marcador: topo = líder; atraso em relação ao líder desce o pino.
        const lag = Math.min(SCREEN.PLAYFIELD_HEIGHT - 40, ((leaderDist - row.dist) / 8) | 0)
        this.sprites.fillStyle(slotColor(row.slot), row.alive ? 1 : 0.35)
        this.sprites.fillRect(SCREEN.WIDTH - 14, 12 + lag, 8, 8)
        const text = this.sideTexts[i]!
        text.setVisible(true)
        text.setPosition(SCREEN.WIDTH - 19, 24 + i * 12)
        text.setTint(row.alive ? 0xffffff : 0x777777)
        text.setText(`P${row.slot + 1}`)
      }
      for (let i = standings.length; i < this.sideTexts.length; i++) this.sideTexts[i]!.setVisible(false)

      const msLeft = this.raceStartAt - performance.now()
      if (msLeft > 0) {
        setPixelSize(this.centerText, 48)
        setPixelText(this.centerText, String(Math.ceil(msLeft / 1000)))
      } else if (this.raceFinished()) {
        setPixelSize(this.centerText, 16)
        const rematchHint = this.race.mySlot === 0 ? 'R: revanche · ' : 'aguarde a revanche · '
        setPixelText(this.centerText, `FIM DA CORRIDA\n\n${this.racePodium()}\n\n${rematchHint}ESC: menu`)
      } else if (!sim.gameOver && msLeft > -800) {
        setPixelSize(this.centerText, 24)
        setPixelText(this.centerText, 'JÁ!')
      } else if (sim.gameOver) {
        setPixelSize(this.centerText, 12)
        setPixelText(this.centerText, 'VOCÊ CAIU\naguardando os outros...')
      } else {
        setPixelSize(this.centerText, 16)
        setPixelText(this.centerText, '')
      }
    }

    this.endImageFrame()

    if (this.debugText.visible) {
      const line = sim.worldLineOf(0)
      this.debugText.setText(
        `fps ${Math.round(this.game.loop.actualFps)} tick ${sim.tick}  [${this.audio.status}]\n` +
          `seed ${this.host.seed} chunk ${(line / WORLD.CHUNK_LINES) | 0} line ${line}\n` +
          `spd ${sim.scrollSpeed} x ${fpToPx(plane?.x ?? 0)} fuel ${Math.round((fuel / FUEL.TANK) * 100)}%`,
      )
    }
  }
}

/** Gancho global de inspeção — teste de fumaça e depuração manual. */
declare global {
  interface Window {
    __pixelraid?: {
      seed: number
      tick: () => number
      /** Tick autoritativo — no guest de co-op, `tick` inclui a previsão. */
      authTick: () => number
      checksum: () => number
      gameOver: () => boolean
      distancePx: () => number
      sections: () => number
      planeX: () => number
      terrainSpan: (line: number) => { leftPx: number; rightPx: number; safe: boolean }
      ghosts: () => number
      transport: () => { open: number; total: number; label: string } | null
      replay: () => unknown
    }
  }
}
